import AppsIcon from '@material-design-icons/svg/filled/apps.svg?react';
import RestoreFromTrashIcon from '@material-design-icons/svg/filled/restore_from_trash.svg?react';
import { useStore } from '@tanstack/react-store';
import type { SortingState } from '@tanstack/react-table';
import { isLeft, isRight } from 'fp-ts/Either';
import { use } from 'react';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { DataTable } from '../components/common/DataTable';
import { TypedStatusBadge } from '../components/common/StatusBadge';
import { DeleteDappActionButton } from '../components/dapps/DeleteDappActionButton';
import { DeleteDappDialog } from '../components/dapps/DeleteDappDialog';
import {
  ListEmptyState,
  ListHeader,
  ListPager,
  ListShell,
  listColumn,
  RelativeTime,
  RowList,
} from '../components/management/ListShell';
import {
  ListToolbar,
  SegmentedControl,
  SortMenu,
  type SortOption,
  ToolbarSearch,
  ToolbarSelect,
  toolbarControlClassName,
} from '../components/management/ListToolbar';
import { ManagementPageLayout } from '../components/management/ManagementPageLayout';
import { S3Image } from '../components/S3Image';
import { TRPCContext } from '../contexts/TRPC';
import { useDappDeletion } from '../hooks/useDappDeletion';
import { useManagementCrud } from '../hooks/useManagementCrud';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { usePageTitle } from '../hooks/usePageTitle';
import type { QueryLoadContext } from '../hooks/usePaginatedQueryLoader';
import { useRequireAuth } from '../hooks/useRequireAuth';
import { useUqlUrlSync } from '../hooks/useUqlUrlSync';
import { formatVersionLabel } from '../lib/formatters';
import { cn } from '../lib/utils';
import {
  type DappDetail,
  type DappsPageState,
  dappsPageStore,
  resetFilters,
  setDapps,
  setDappsQueryState,
  setLoading,
  setPage,
  setPaginationInfo,
  setSorting,
  updateFilter,
} from '../stores/dappsPageStore';
import { getAdminDappPath } from '../utils/dappRoutes';
import { buildDappsUql, parseDappsUql } from '../utils/dappsPageUrlState';
import { captureAndReportError } from '../utils/errorReporting';
import {
  canAccessHubspotTicketPages,
  hasAdminAccess,
  hasSuperadminAccess,
} from '../utils/roleUtils';

const PAGE_SIZE = 20;

type DappsFilters = DappsPageState['filters'];

const STATUS_SEGMENTS: ReadonlyArray<{
  value: DappsFilters['status'];
  label: string;
}> = [
  { value: 'all', label: 'All' },
  { value: 'UnderReview', label: 'Under review' },
  { value: 'Published', label: 'Published' },
  { value: 'Draft', label: 'Draft' },
  { value: 'Rejected', label: 'Rejected' },
  { value: 'Archived', label: 'Archived' },
];

const PUBLISHER_TYPE_OPTIONS: ReadonlyArray<{
  value: DappsFilters['publisherType'];
  label: string;
}> = [
  { value: 'all', label: 'All publishers' },
  { value: 'Organization', label: 'Organizations' },
  { value: 'Individual', label: 'Individuals' },
];

const NFT_OPTIONS: ReadonlyArray<{
  value: DappsFilters['hasNft'];
  label: string;
}> = [
  { value: 'all', label: 'NFT: any' },
  { value: 'true', label: 'Has app NFT' },
  { value: 'false', label: 'No app NFT' },
];

const SORT_OPTIONS: ReadonlyArray<SortOption> = [
  { id: 'created', desc: true, label: 'Newest first' },
  { id: 'created', desc: false, label: 'Oldest first' },
  { id: 'updated', desc: true, label: 'Recently updated' },
  { id: 'dappName', desc: false, label: 'Name A–Z' },
  { id: 'publisherName', desc: false, label: 'Publisher A–Z' },
  { id: 'status', desc: false, label: 'Status' },
];

const API_SORT_IDS = new Set([
  'created',
  'updated',
  'status',
  'dappName',
  'publisherName',
]);

interface DappsPageLoadContext {
  filters: DappsFilters;
  sorting: SortingState;
  pagination: { currentPage: number; totalPages: number; total: number };
}

function publishedVersion(dapp: DappDetail) {
  return dapp.publishedVersionName !== null ||
    dapp.publishedVersionCode !== null
    ? formatVersionLabel(dapp.publishedVersionName, dapp.publishedVersionCode)
    : null;
}

/** All dapps across publishers: find one fast, triage by status, jump in. */
export function DappsPage() {
  const { authState, canRender } = useRequireAuth({
    requireRole: 'hubspotTickets',
  });
  const navigate = useNavigate();
  const trpc = use(TRPCContext);
  usePageTitle('Dapps | Publishing Portal');
  const isNarrow = useMediaQuery('(max-width: 767px)');

  const dapps = useStore(dappsPageStore, (state) => state.dapps);
  const canManageDappDeletion =
    authState.type === 'Authenticated' &&
    hasAdminAccess(authState.user.role, authState.user.email);
  const canSeeDeletedDapps =
    authState.type === 'Authenticated' &&
    hasSuperadminAccess(authState.user.role, authState.user.email);

  const loadDapps = async ({
    isCurrentRequest,
    filters,
    sorting,
    pagination,
  }: QueryLoadContext & DappsPageLoadContext) => {
    if (!trpc?.client) return;

    try {
      const apiSorting = sorting
        .filter((sort) => API_SORT_IDS.has(sort.id))
        .map((sort) => ({
          id: sort.id as
            | 'created'
            | 'updated'
            | 'status'
            | 'dappName'
            | 'publisherName',
          desc: sort.desc,
        }));

      const result = await trpc.client.listAllDapps.query({
        filters: {
          status: filters.status,
          publisherType: filters.publisherType,
          hasNft:
            filters.hasNft === 'all' ? undefined : filters.hasNft === 'true',
          search: filters.search || undefined,
        },
        pagination: { page: pagination.currentPage, limit: PAGE_SIZE },
        sorting: apiSorting.length > 0 ? apiSorting : undefined,
      });

      if (!isCurrentRequest()) return;

      if (isRight(result)) {
        setDapps(result.right.dapps);
        setPaginationInfo(
          pagination.currentPage,
          result.right.pagination.totalPages,
          result.right.pagination.total,
        );
      } else if (isLeft(result)) {
        toast.error(result.left.message);
      }
    } catch (error) {
      if (!isCurrentRequest()) return;

      captureAndReportError(error, {
        operation: 'dappsPage.loadDapps',
        component: 'DappsPage',
        filters,
        page: pagination.currentPage,
      });
      toast.error('Failed to load dapps');
    }
  };

  const {
    filters,
    sorting,
    pagination,
    isLoading,
    reload: reloadDapps,
  } = useManagementCrud({
    enabled: canRender,
    store: dappsPageStore,
    actions: {
      updateFilter,
      setSorting,
      setPage,
      setPaginationInfo,
      setLoading,
    },
    loadPage: loadDapps,
  });

  useUqlUrlSync({
    enabled: canRender,
    parse: parseDappsUql,
    build: buildDappsUql,
    apply: setDappsQueryState,
    current: { filters, sorting, page: pagination.currentPage },
  });

  const {
    deleteModalDapp,
    deleteReason,
    isDeleting,
    openDeleteDialog,
    closeDeleteDialog,
    setDeleteReason,
    confirmDelete,
  } = useDappDeletion({
    component: 'DappsPage',
    operation: 'dappsPage.softDeleteDapp',
    onDeleted: reloadDapps,
  });

  if (!canRender) return null;

  const activeFilterCount = [
    filters.publisherType !== 'all',
    filters.hasNft !== 'all',
  ].filter(Boolean).length;
  const hasActiveFilters =
    activeFilterCount > 0 || filters.status !== 'all' || filters.search !== '';
  const isEmpty = !isLoading && dapps.length === 0;
  const openDapp = (dapp: DappDetail) => navigate(getAdminDappPath(dapp));

  const columns = [
    listColumn<DappDetail>(
      'dappName',
      'App',
      320,
      { sortable: true },
      (dapp) => <DappIdentity dapp={dapp} />,
    ),
    listColumn<DappDetail>(
      'status',
      'Status',
      140,
      { sortable: true },
      (dapp) => <TypedStatusBadge type="dapp" status={dapp.status} />,
    ),
    listColumn<DappDetail>(
      'publishedVersion',
      'Live version',
      140,
      {},
      (dapp) => {
        const version = publishedVersion(dapp);
        return version ? (
          <span className="text-sm tabular-nums text-neutral-200">
            {version}
          </span>
        ) : (
          <span className="text-xs text-neutral-500">Not published</span>
        );
      },
    ),
    listColumn<DappDetail>(
      'publisherName',
      'Publisher',
      220,
      { sortable: true },
      (dapp) => <PublisherCell dapp={dapp} />,
    ),
    listColumn<DappDetail>('releases', 'Releases', 190, {}, (dapp) => (
      <ReleasesCell dapp={dapp} />
    )),
    listColumn<DappDetail>(
      'created',
      'Created',
      110,
      { sortable: true, descFirst: true },
      (dapp) => (
        <RelativeTime
          timestamp={dapp.created}
          className="text-xs text-neutral-300"
        />
      ),
    ),
    ...(canManageDappDeletion
      ? [
          listColumn<DappDetail>(
            'actions',
            <span className="sr-only">Actions</span>,
            56,
            { className: '!px-2 text-right' },
            (dapp) => (
              <DeleteDappActionButton dapp={dapp} onClick={openDeleteDialog} />
            ),
          ),
        ]
      : []),
  ];

  return (
    <>
      <ManagementPageLayout
        authState={authState}
        canAccess={canAccessHubspotTicketPages}
        className="flex-1 overflow-y-auto p-4 md:p-6"
      >
        <div className="space-y-4">
          <header className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <div className="hidden size-10 shrink-0 items-center justify-center rounded-xl bg-neutral-800 sm:flex">
                <AppsIcon className="size-5 fill-current text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="text-xl font-semibold tracking-tight text-white sm:text-2xl">
                  Dapps
                </h1>
                <p className="text-sm text-neutral-400">
                  Every app across publishers, with its Portal state.
                </p>
              </div>
            </div>
            {canSeeDeletedDapps && (
              <Link
                to="/super/deleted-dapps"
                className={cn(toolbarControlClassName, 'text-sm')}
              >
                <RestoreFromTrashIcon className="size-4 fill-current text-neutral-400" />
                Deleted dapps
              </Link>
            )}
          </header>

          <ListToolbar
            compact={isNarrow}
            activeFilterCount={activeFilterCount}
            hasActiveFilters={hasActiveFilters}
            onClearFilters={resetFilters}
            segments={
              <SegmentedControl
                label="Status"
                value={filters.status}
                options={STATUS_SEGMENTS}
                onChange={(value) => updateFilter('status', value)}
                className="w-full sm:w-auto"
              />
            }
            search={
              <ToolbarSearch
                label="Search dapps"
                placeholder="Search name, package, publisher…"
                value={filters.search}
                onChange={(value) => updateFilter('search', value)}
              />
            }
            filters={
              <>
                <ToolbarSelect
                  label="Publisher type"
                  value={filters.publisherType}
                  options={PUBLISHER_TYPE_OPTIONS}
                  onChange={(value) => updateFilter('publisherType', value)}
                />
                <ToolbarSelect
                  label="App NFT"
                  value={filters.hasNft}
                  options={NFT_OPTIONS}
                  onChange={(value) => updateFilter('hasNft', value)}
                />
              </>
            }
            trailing={
              <SortMenu
                label="Sort dapps"
                options={SORT_OPTIONS}
                sorting={sorting}
                onSortingChange={setSorting}
              />
            }
          />

          <ListShell>
            <ListHeader
              total={pagination.total}
              noun={['dapp', 'dapps']}
              isLoading={isLoading}
              onReload={() => {
                void reloadDapps();
              }}
            />
            {isEmpty ? (
              <ListEmptyState
                icon={AppsIcon}
                title="No dapps yet"
                hint="Apps appear here as publishers create them."
                hasActiveFilters={hasActiveFilters}
                onClearFilters={resetFilters}
              />
            ) : isNarrow ? (
              <RowList
                items={dapps}
                getId={(dapp) => dapp.id}
                isLoading={isLoading}
                onSelect={openDapp}
                renderRow={(dapp) => <DappRow dapp={dapp} />}
                renderTrailing={
                  canManageDappDeletion
                    ? (dapp) => (
                        <DeleteDappActionButton
                          dapp={dapp}
                          onClick={openDeleteDialog}
                          iconClassName="size-5"
                        />
                      )
                    : undefined
                }
              />
            ) : (
              <DataTable
                data={dapps}
                columns={columns}
                isLoading={isLoading}
                onRowClick={openDapp}
                getRowId={(dapp) => dapp.id}
                sorting={sorting}
                onSortingChange={setSorting}
                enableSorting
                headerRowClassName="bg-neutral-900/80"
              />
            )}
            <ListPager
              pagination={pagination}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
            />
          </ListShell>
        </div>
      </ManagementPageLayout>

      <DeleteDappDialog
        dapp={deleteModalDapp}
        deleteReason={deleteReason}
        isDeleting={isDeleting}
        onDeleteReasonChange={setDeleteReason}
        onClose={closeDeleteDialog}
        onConfirm={confirmDelete}
      />
    </>
  );
}

function DappIcon({
  dapp,
  className,
}: {
  dapp: DappDetail;
  className?: string;
}) {
  return (
    <S3Image
      src={dapp.dappIconUrl}
      thumbnailSrc={dapp.dappIconThumbnailUrl}
      alt=""
      aria-hidden="true"
      className={cn(
        'size-10 shrink-0 rounded-lg border border-neutral-700/50 object-cover',
        className,
      )}
      fallback={
        <div
          className={cn(
            'flex size-10 shrink-0 items-center justify-center rounded-lg border border-neutral-700/50 bg-neutral-800',
            className,
          )}
        >
          <AppsIcon className="size-5 fill-current text-neutral-500" />
        </div>
      }
    />
  );
}

/** Icon, name, and package: the cell an admin scans first. */
function DappIdentity({ dapp }: { dapp: DappDetail }) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <DappIcon dapp={dapp} />
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold text-white">
          {dapp.dappName}
        </div>
        <div className="truncate font-mono text-xs text-neutral-400">
          {dapp.packageName ?? 'No package'}
        </div>
      </div>
    </div>
  );
}

function PublisherCell({ dapp }: { dapp: DappDetail }) {
  return (
    <div className="min-w-0">
      <Link
        to={`/publishers/${dapp.publisherId}`}
        onClick={(event) => event.stopPropagation()}
        className="block truncate text-sm text-neutral-200 transition-colors hover:text-teal-300"
        title="Open publisher"
      >
        {dapp.publisherName || 'Unknown publisher'}
      </Link>
      <div className="truncate text-xs text-neutral-500">
        {dapp.publisherType}
      </div>
    </div>
  );
}

function ReleasesCell({ dapp }: { dapp: DappDetail }) {
  const latest = dapp.latestRelease;

  return (
    <div className="flex min-w-0 items-center gap-2">
      <span className="text-sm tabular-nums text-neutral-200">
        {dapp.releaseCount}
      </span>
      {latest && (
        <>
          <span className="truncate text-xs tabular-nums text-neutral-500">
            latest {formatVersionLabel(latest.versionName, latest.versionCode)}
          </span>
          <TypedStatusBadge
            type="release"
            status={latest.status}
            className="shrink-0"
          />
        </>
      )}
    </div>
  );
}

/** Phone row: name and age, package and publisher, then state. */
function DappRow({ dapp }: { dapp: DappDetail }) {
  const version = publishedVersion(dapp);

  return (
    <>
      <DappIcon dapp={dapp} className="mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="truncate text-sm font-semibold text-white">
            {dapp.dappName}
          </span>
          <RelativeTime
            timestamp={dapp.created}
            className="shrink-0 text-xs text-neutral-500"
          />
        </div>
        <div className="mt-0.5 truncate text-xs text-neutral-400">
          <span className="font-mono">{dapp.packageName ?? 'No package'}</span>
          <span className="mx-1 text-neutral-600">·</span>
          <span>{dapp.publisherName}</span>
        </div>
        <div className="mt-2 flex items-center justify-between gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            <TypedStatusBadge type="dapp" status={dapp.status} />
            {version && (
              <span className="text-xs tabular-nums text-neutral-300">
                {version}
              </span>
            )}
          </div>
          <span className="shrink-0 text-xs tabular-nums text-neutral-500">
            {dapp.releaseCount}{' '}
            {dapp.releaseCount === 1 ? 'release' : 'releases'}
          </span>
        </div>
      </div>
    </>
  );
}
